import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, MapPin, FileText, BookOpen, ClipboardList, Calendar as CalIcon } from "lucide-react";
import { formatEventTitle, isCancelledEvent } from "@/lib/utils";
import { EventDetailModal } from "@/components/EventDetailModal";
import { IcalShareCard } from "@/components/IcalShareCard";

const weekday = (d) =>
  ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"][(d.getDay() + 6) % 7];

const fullMonth = (d) =>
  ["Januari", "Februari", "Mars", "April", "Maj", "Juni", "Juli", "Augusti", "September", "Oktober", "November", "December"][d.getMonth()];

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });

const fmtDate = (d) =>
  `${d.getDate()} ${["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"][d.getMonth()]}`;

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const CATEGORY_META = {
  test: { label: "Prov", color: "#DC2626", bg: "#FEE2E2", Icon: FileText },
  homework: { label: "Läxa", color: "#E07A5F", bg: "#FDF2EE", Icon: BookOpen },
  assignment: { label: "Uppgift", color: "#2A9D8F", bg: "#E7F4F1", Icon: ClipboardList },
  event: { label: "Händelse", color: "#3D5A80", bg: "#EEF2F7", Icon: CalIcon },
};

// Sortera: prov först, sedan läxor, sedan vanliga händelser efter starttid
const sortEvents = (a, b) => {
  const order = { test: 0, homework: 1, assignment: 2, event: 3 };
  const oa = order[a.category] ?? 4;
  const ob = order[b.category] ?? 4;
  if (oa !== ob) return oa - ob;
  return (a.start || "").localeCompare(b.start || "");
};

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => {
    // Djuplänk från pushnotis: /kalender?date=YYYY-MM-DD öppnar rätt vecka
    try {
      const p = new URLSearchParams(window.location.search).get("date");
      if (p) {
        const d = new Date(`${p}T12:00:00`);
        if (!isNaN(d)) return d;
      }
    } catch { /* tyst */ }
    return new Date();
  });
  const [view, setView] = useState("week"); // week | month
  const [loading, setLoading] = useState(true);
  const [openEventId, setOpenEventId] = useState(null);
  const [showLessons, setShowLessons] = useState(() => {
    try { return localStorage.getItem("cal_show_lessons") === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("cal_show_lessons", showLessons ? "1" : "0"); } catch (_) {}  }, [showLessons]);

  const load = async () => {
    setLoading(true);
    try {
      const start = new Date(cursor);
      start.setDate(start.getDate() - 14);
      const end = new Date(cursor);
      end.setDate(end.getDate() + 60);
      const { data } = await api.get(
        `/events?from_=${start.toISOString()}&to=${end.toISOString()}&type=all&limit=1000&include_lessons=${showLessons ? "true" : "false"}`,
      );
      setEvents(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [showLessons, cursor]);

  useEffect(() => {
    // Markera alla "nya" events som lästa när användaren öppnar kalender (med 2 sek delay
    // så badgen syns en stund)
    const t = setTimeout(() => {
      api.post("/events/mark-seen").catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  // (Auto-scroll till "idag" har tagits bort — vy:n börjar nu alltid på `cursor`
  // som default = idag, så scroll behövs inte.)

  const byDay = useMemo(() => {
    const map = new Map();
    const add = (k, e) => {
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    };
    events.forEach((e) => {
      if (e.all_day) {
        // Heldagar: använd rå datumsträng (tz-konvertering flyttar annars
        // vinterdatum en dag bakåt) + expandera flerdagars-event (lov) till
        // varje dag i spannet. ICS-heldagar har exklusivt slut (T00:00) →
        // backa en dag i så fall.
        const startStr = (e.start || "").slice(0, 10);
        let endStr = (e.end || e.start || "").slice(0, 10);
        if ((e.end || "").includes("T00:00") && endStr > startStr) {
          const ex = new Date(`${endStr}T12:00:00`);
          ex.setDate(ex.getDate() - 1);
          endStr = `${ex.getFullYear()}-${String(ex.getMonth() + 1).padStart(2, "0")}-${String(ex.getDate()).padStart(2, "0")}`;
        }
        const d = new Date(`${startStr}T12:00:00`);
        const end = new Date(`${endStr}T12:00:00`);
        for (let i = 0; d <= end && i <= 60; i += 1) {
          add(dayKey(d), e);
          d.setDate(d.getDate() + 1);
        }
      } else {
        add(dayKey(new Date(e.start)), e);
      }
    });
    return map;
  }, [events]);

  const weekDays = useMemo(() => {
    // Visa ALLTID 14 dagar framåt från `cursor` (default = idag).
    // Tidigare logik startade veckan på måndag → på söndag såg man bara
    // dagens händelser eftersom resten av veckan var i det förflutna.
    // Med 14 dagar framåt har man alltid en relevant planeringshorisont.
    const start = new Date(cursor);
    start.setHours(0, 0, 0, 0);
    return [...Array(14)].map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  const monthGrid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    return [...Array(42)].map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  return (
    <div className="px-5 pt-3" data-testid="calendar-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-eyebrow">Kalender</p>
          <h1 className="font-display text-2xl font-medium mt-0.5" data-testid="calendar-title">
            {fullMonth(cursor)} {cursor.getFullYear()}
          </h1>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              const d = new Date(cursor);
              if (view === "week") d.setDate(d.getDate() - 14);
              else d.setMonth(d.getMonth() - 1);
              setCursor(d);
            }}
            className="p-2 rounded-full hover:bg-[color:var(--bg-elevated)]"
            data-testid="calendar-prev-button"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => setCursor(new Date())}
            className="text-xs px-3 py-1.5 rounded-full bg-[color:var(--bg-elevated)] font-medium"
            data-testid="calendar-today-button"
          >
            Idag
          </button>
          <button
            onClick={() => {
              const d = new Date(cursor);
              if (view === "week") d.setDate(d.getDate() + 14);
              else d.setMonth(d.getMonth() + 1);
              setCursor(d);
            }}
            className="p-2 rounded-full hover:bg-[color:var(--bg-elevated)]"
            data-testid="calendar-next-button"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="mb-3">
        <TabsList className="grid grid-cols-2 w-full bg-[color:var(--bg-elevated)]">
          <TabsTrigger value="week" data-testid="view-week-tab">14 dagar</TabsTrigger>
          <TabsTrigger value="month" data-testid="view-month-tab">Månad</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mb-4 flex items-center justify-end">
        <label className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)] cursor-pointer select-none" data-testid="show-lessons-toggle">
          <input
            type="checkbox"
            checked={showLessons}
            onChange={(e) => setShowLessons(e.target.checked)}
            className="rounded border-[color:var(--border)] text-[color:var(--moss)] focus:ring-[color:var(--moss)] w-3.5 h-3.5"
          />
          Visa skol-lektioner
        </label>
      </div>

      {view === "week" && (
        <div className="space-y-3 stagger">
          {weekDays.map((d) => {
            const dayEvents = (byDay.get(dayKey(d)) || []).slice().sort(sortEvents);
            const isToday = dayKey(d) === dayKey(new Date());
            return (
              <div key={dayKey(d)} className="fade-up" data-testid={`day-${dayKey(d)}`}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span
                    className={`font-display text-2xl font-medium ${
                      isToday ? "text-[color:var(--moss)]" : ""
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  <span className="text-xs uppercase tracking-wider text-[color:var(--text-secondary)]">
                    {weekday(d)}
                  </span>
                  {isToday && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--moss)] text-white">
                      Idag
                    </span>
                  )}
                </div>
                {dayEvents.length === 0 ? (
                  <div className="text-xs text-[color:var(--text-tertiary)] pl-1 pb-2">Inget planerat</div>
                ) : (
                  <ul className="space-y-1.5">
                    {dayEvents.map((e) => (
                      <EventChip key={e.id} e={e} onClick={() => setOpenEventId(e.id)} />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === "month" && (
        <div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
            {["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map((w) => (
              <div key={w}>{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthGrid.map((d) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = dayKey(d) === dayKey(new Date());
              const dayEvents = byDay.get(dayKey(d)) || [];
              const hasTest = dayEvents.some((e) => e.category === "test");
              const hasHomework = dayEvents.some((e) => e.category === "homework");
              const hasAssignment = dayEvents.some((e) => e.category === "assignment");
              return (
                <button
                  key={dayKey(d)}
                  onClick={() => {
                    setCursor(d);
                    setView("week");
                  }}
                  className={`aspect-square flex flex-col items-center justify-start p-1.5 rounded-lg text-xs relative ${
                    inMonth ? "text-[color:var(--text-primary)]" : "text-[color:var(--text-tertiary)]"
                  } ${isToday ? "bg-[color:var(--moss)] text-white" : "hover:bg-[color:var(--bg-elevated)]"}`}
                  data-testid={`month-day-${dayKey(d)}`}
                >
                  <span className="font-medium">{d.getDate()}</span>
                  {/* Kategoriindikatorer — prov (röd), läxa (orange), feed-färg (övrigt) */}
                  <div className="flex gap-0.5 mt-1 flex-wrap justify-center max-w-full">
                    {hasTest && (
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: isToday ? "#FEE2E2" : "#DC2626" }}
                        title="Prov"
                      />
                    )}
                    {hasHomework && (
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: isToday ? "#FDF2EE" : "#E07A5F" }}
                        title="Läxa"
                      />
                    )}
                    {hasAssignment && (
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: isToday ? "#E7F4F1" : "#2A9D8F" }}
                        title="Uppgift"
                      />
                    )}
                    {dayEvents
                      .filter((e) => e.category === "event")
                      .slice(0, 3)
                      .map((e) => (
                        <span
                          key={e.id}
                          className="w-1 h-1 rounded-full"
                          style={{ backgroundColor: e.feed_color }}
                        />
                      ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading && (
        <p className="text-xs text-[color:var(--text-tertiary)] text-center mt-4">Laddar…</p>
      )}

      <div className="mt-6">
        <IcalShareCard />
      </div>

      {openEventId && (
        <EventDetailModal
          eventId={openEventId}
          onClose={() => setOpenEventId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

const EventChip = ({ e, onClick }) => {
  const meta = CATEGORY_META[e.category] || CATEGORY_META.event;
  const Icon = meta.Icon;
  const isSchoolwork = e.category === "homework" || e.category === "test" || e.category === "assignment";
  return (
    <li
      onClick={onClick}
      className="card-soft p-3 flex items-start gap-3 relative cursor-pointer hover:bg-[color:var(--bg-elevated)] transition-colors"
      data-testid={`event-${e.id}`}
    >
      <div
        className="w-1 self-stretch rounded-full flex-shrink-0"
        style={{ backgroundColor: isSchoolwork ? meta.color : e.feed_color }}
      />
      {isSchoolwork && (
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: meta.bg, color: meta.color }}
        >
          <Icon size={13} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {isSchoolwork && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide"
              style={{ backgroundColor: meta.bg, color: meta.color }}
              data-testid={`event-cat-${e.id}`}
            >
              {meta.label}
            </span>
          )}
          <p className={`text-sm font-medium leading-snug ${isCancelledEvent(e) ? "line-through text-[color:var(--text-tertiary)]" : ""}`}>{formatEventTitle(e)}</p>
          {e.is_new && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E07A5F] text-white font-semibold uppercase tracking-wide"
              data-testid={`event-new-badge-${e.id}`}
            >
              Ny
            </span>
          )}
        </div>
        <p className="text-xs text-[color:var(--text-secondary)] mt-0.5">
          {e.all_day
            ? "Heldag"
            : e.end
              ? `${fmtTime(e.start)}–${fmtTime(e.end)}`
              : fmtTime(e.start)}
          {e.feed_owner && e.feed_owner.toLowerCase() !== "familjen" && ` · ${e.feed_owner}`}
        </p>
        {e.location && (
          <p className="text-xs text-[color:var(--text-tertiary)] mt-0.5 flex items-center gap-1">
            <MapPin size={11} /> {e.location}
          </p>
        )}
      </div>
    </li>
  );
};
